import { z } from "zod";

import {
  BaseJsonSchema,
  JsonSchema,
  JsonSchemaMetadata,
  ObjectJsonSchema,
} from "./jsonSchema";

export const Agent = z.object({
  id: z.string(),
  name: z.string(),
});
export type Agent = z.infer<typeof Agent>;

export const AgentColumn = z.object({
  field: z.string(),
  title: z.string(),
  description: z.string(),
  datatype: z.string(),
});
export type AgentColumn = z.infer<typeof AgentColumn>;

export const AgentType = z.object({
  key: z.string(),
  label: z.string(),
});
export type AgentType = z.infer<typeof AgentType>;

export const citationSchema = z.preprocess(
  (citation) => {
    if (
      citation &&
      typeof citation === "object" &&
      "document_id" in citation &&
      !("file_id" in citation)
    ) {
      return {
        ...citation,
        file_id: citation.document_id,
      };
    }

    return citation;
  },
  z.object({
    file_id: z.string().uuid(),
    page_index: z.number().int().nonnegative(),
    citation_key: z.string(),
  }),
);
export type Citation = z.infer<typeof citationSchema>;
export const citationsSchema = z.array(citationSchema);

const inlineCitationCoordinates = z.object({
  left: z.number(),
  top: z.number(),
  width: z.number(),
  height: z.number(),
});

const inlineCitationFile = z.object({
  id: z.string().uuid(),
  file_name: z.string().default(""),
});

const inlineCitationAnnotation = z.object({
  id: z.string(),
  file: inlineCitationFile,
  page_index: z.number().int().nonnegative(),
  coordinates: z.array(inlineCitationCoordinates).default([]),
});

export const ApiModelInlineCitation = z.object({
  key: z.string(),
  citation_type: z.literal("FILE_PREVIEW").default("FILE_PREVIEW"),
  annotations: z.array(inlineCitationAnnotation).default([]),
});
export type ApiModelInlineCitation = z.infer<typeof ApiModelInlineCitation>;

export const TabularConfig = z.object({
  __typename: z.literal("tabular"),
  data_property: z.string(),
  name: z.string(),
  columns: z.array(AgentColumn),
});
export type TabularConfig = z.infer<typeof TabularConfig>;

export const TaskArtifactOutput = z
  .object({
    markdown: z.string(),
    citations: citationsSchema.default([]),
    inline_citations: z.array(ApiModelInlineCitation).default([]),
  })
  .passthrough();

export const MarkdownJsonSchema = BaseJsonSchema.extend({
  type: z.literal("string"),
  metadata: JsonSchemaMetadata.extend({
    UI_TYPE: z.literal("markdown"),
  }),
});
export type MarkdownJsonSchema = z.infer<typeof MarkdownJsonSchema>;

export const CitationsJsonSchema = BaseJsonSchema.extend({
  type: z.literal("array"),
  metadata: JsonSchemaMetadata.extend({
    UI_TYPE: z.literal("citations"),
  }),
});

export const TaskArtifactOutputJsonSchema = ObjectJsonSchema.extend({
  properties: z
    .object({
      markdown: MarkdownJsonSchema,
      citations: JsonSchema,
    })
    .and(z.record(z.string(), JsonSchema)),
});
export type TaskArtifactOutputJsonSchema = z.infer<
  typeof TaskArtifactOutputJsonSchema
>;

export const AgentMessageUserValidationStatus = z.enum(["ACCEPTED", "UNSET"]);
export type AgentMessageUserValidationStatus = z.infer<
  typeof AgentMessageUserValidationStatus
>;

export const Author = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  title: z.string().nullable().optional(),
});
export type Author = z.infer<typeof Author>;

export const AgentMessageUserValidation = z.object({
  status: AgentMessageUserValidationStatus,
  rejectionReason: z.string().nullish(),
  userId: z.string(),
  createdAt: z.string(),
  author: Author,
});
export type AgentMessageUserValidation = z.infer<
  typeof AgentMessageUserValidation
>;

export const AgentMessageRevision = z.object({
  reason: z.string().nullish(),
  revisionId: z.string(),
});
export type AgentMessageRevision = z.infer<typeof AgentMessageRevision>;

export const AgentMessage = z.object({
  id: z.string(),
  name: z.string(),
  schema: z.unknown(),
  content: z.object({
    markdownTitle: z.string().nullable().optional(),
    markdownBody: z.string().nullable().optional(),
    taskArtifactOutput: TaskArtifactOutput.nullable().optional(),
  }),
  userValidation: AgentMessageUserValidation.nullish(),
  revision: AgentMessageRevision.nullish(),
});
export type AgentMessage = z.infer<typeof AgentMessage>;

export type AgentMessageContent = z.infer<typeof AgentMessage>["content"];
